import { Box, Button, Grid, TextField } from "@mui/material";
import classes from "./TrainingNotes.module.css";
import { fontStyles } from "components/Common/commonStyles";
import FieldLabel from "components/LearnPoint/DTR/FieldLabel";
import React, { useEffect, useRef, useState } from "react";
import { useBlocker } from "react-router-dom";
import Autocomplete from "@mui/material/Autocomplete";
import dayjs from "dayjs";
// ASTT-7319: same DatePicker/LocalizationProvider/AdapterDayjs pattern
// already used elsewhere in this same DTR folder (RosterDetails.js).
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { PickersDay } from "@mui/x-date-pickers/PickersDay";
import {
  getDTRTrainingNotes,
  saveDTRTrainingNotesWithAttachments,
  getDTRAttachmentsByParentId,
  getCurriculumStatus,
  sendDTREmail,
  previewDtrEmail,
  downloadAttachmentImageAsBlobUrl,
  getDTRListClassRosterIdWise,
} from "services/learnPointUserServices";
import moment from "moment";
import PropTypes from "prop-types";
import BackdropComponent from "components/Spinner/Spinner";
import SnackBar from "components/SnackBar/SnackBar";
import { ThemeProvider, styled } from "@mui/material/styles";
import dtrTheme from "components/LearnPoint/DTR/dtrTheme";
import DtrEmailDistro from "components/LearnPoint/DTR/DtrEmailDistro";
import AIPrompt from "components/Common/AIPrompt/AIPrompt";
import { isEmptyString } from "components/RequestForAccess/RequestForAccess";
import SaveOrSendButton from "components/LearnPoint/DTR/SaveOrSendButton";
// ASTT-7319: Preview button, per direct stakeholder-demo feedback --
// shared with HandOffReport.js's own identical use.
import EmailPreviewDialog from "components/LearnPoint/DTR/EmailPreviewDialog";
// RichTextField/stripHtml/sanitizeEditorHtml are shared with HandOffReport.js.
import RichTextField, {
  stripHtml,
  sanitizeEditorHtml,
} from "components/LearnPoint/DTR/RichTextEditorField";
import DTRAttachmentList from "components/LearnPoint/DTR/DTRAttachmentList";
import DTRAttachmentSummary from "components/LearnPoint/DTR/DTRAttachmentSummary";
import UnsavedChangesBlockerDialog from "components/LearnPoint/DTR/UnsavedChangesBlockerDialog";

const MAX_LENGTH = 4000;

const formatDate = (date, formatString) => moment(date).format(formatString);
const initialTrainingNotesDetails = {
  ClassRosterId: "",
  DTRDate: "",
  Successes: "",
  Challenges: "",
  TopicsCovered: "",
  ClassRiskCategory: "",
  ClassRiskNotes: "",
  LP_DTRDistroId: "",
  EmailDistro: "",
  DTRStatus: "",
  CreatedDate: "",
  CreatedBy: "",
  ModifiedDate: "",
  ModifiedBy: "",
};

// ASTT-7319: fontFamily/letterSpacing were hardcoded literals here,
// duplicating components/Common/commonStyles.js's fontStyles -- the
// actual established, app-wide typography convention (confirmed via the
// full redesign audit), not something specific to this module. Spreading
// it in means any future typography change happens in one place.
const CustomButton = styled(Button)(() => ({
  ...fontStyles,
  fontSize: "12px",
  borderRadius: "8px",
  fontWeight: "600",
  textTransform: "none",
  color: "white",
  backgroundColor: "#3299FF",
  "&:hover": {
    backgroundColor: "#2878CC",
  },
}));

const CustomOutlinedButton = styled(Button)(() => ({
  ...fontStyles,
  fontSize: "12px",
  borderRadius: "8px",
  fontWeight: "600",
  textTransform: "none",
  borderColor: "#3299FF",
  color: "#3299FF",
  padding: "6px 20px",

  "&:hover": {
    borderColor: "#3299FF",
    backgroundColor: "#E0F2FF",
  },
}));

// ASTT-7319 follow-up: @mui/x-date-pickers v5's documented ActionBar
// override slot -- renders inside the calendar popup itself, at the
// bottom, rather than as a floating Cancel/OK bar. Static content, no
// per-instance state needed, so this lives at module scope rather than
// inside TrainingNotes. Accepts the ActionBar prop signature (unused
// here) since MUI calls it with those props regardless.
function DateLegendActionBar() {
  return (
    <Box sx={{ display: "flex", gap: 1.5, p: 1, fontSize: "0.75rem", color: "#607d8b", borderTop: "1px solid #e0e0e0" }}>
      <Box component="span" sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        <Box sx={{ width: 10, height: 10, borderRadius: "2px", backgroundColor: "#DCF5E0", border: "1px solid #2AA154" }} />
        Sent
      </Box>
      <Box component="span" sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        <Box sx={{ width: 10, height: 10, borderRadius: "2px", backgroundColor: "#FFF4D6", border: "1px solid #E0A800" }} />
        Draft
      </Box>
    </Box>
  );
}

export default function TrainingNotes(props) {
  // ASTT-7319 follow-up: onDtrSaved added -- called after a successful
  // Save as Draft/Send as Email, notifying DtrDetails.js (the common
  // ancestor of the DTR count badge and the Roster Details table/View
  // DTRs list, neither of which live in this component) that a refresh
  // is needed. This component's own calendar highlighting is refreshed
  // directly via loadDtrDatesForCalendar() instead, since that state
  // lives here.
  const { rosterId, dtrDate, onDirtyChange, classRequestId, jumpToDtrDate, onDtrSaved, selectedDtrDate, onSelectedDtrDateChange } = props;
  // ASTT-7319: moved to shared dtrTheme.js -- this was previously its own
  // local createTheme() call, byte-identical to HandOffReport.js's own
  // copy, duplicated independently rather than shared.
  const theme = dtrTheme;

  const [trainingNotesDetails, setTrainingNotesDetails] = useState(
    initialTrainingNotesDetails,
  );

  const todayDate = formatDate(new Date(), "YYYY-MM-DD");
  const defaultDate = formatDate(
    dtrDate != null ? dtrDate : new Date(),
    "YYYY-MM-DD",
  );

  const showSnack = (message, severity) => {
    setSnack({
      open: true,
      message: message,
      severity: severity,
    });
  };

  const [snack, setSnack] = useState({
    open: false,
    message: "",
    severity: "",
  });

  const snackReset = () => {
    setSnack({
      open: false,
      message: "",
      severity: "",
    });
  };

  // ASTT-7178: selectedDtrDate/onSelectedDtrDateChange now come from
  // props (lifted up to DtrDetails.js so ClassDetails.js's roster picker
  // can also read the currently-active date for color-coding, mirroring
  // HandOffReport.js's own reportType lift-up) -- this component no
  // longer owns this as local state. defaultDate above is still computed
  // locally (a pure derivation from the dtrDate prop, not stateful) and
  // reused below wherever this needs to reset back to its default.
  // Tracks whichever date's content is actually loaded/displayed (only changes on Load
  // click, not every date-picker keystroke) — kept separate from selectedDtrDate.
  const [loadedDtrDate, setLoadedDtrDate] = useState(defaultDate);
  const [successes, setSuccesses] = useState("");
  const [challenges, setChallenges] = useState("");
  const [topicsCovered, setTopicsCovered] = useState("");
  const [classRiskNotes, setClassRiskNotes] = useState("");
  const [distroId, setDistroId] = useState(0);
  const [successesRevertKey, setSuccessesRevertKey] = useState(0);
  const [challengesRevertKey, setChallengesRevertKey] = useState(0);
  const [topicsCoveredRevertKey, setTopicsCoveredRevertKey] = useState(0);
  const [classRiskNotesRevertKey, setClassRiskNotesRevertKey] = useState(0);

  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState("");
  const [isReadOnly, setIsReadOnly] = useState(false);
  const [curriculumStatusOptions, setCurriculumStatusOptions] = useState([]);
  const [showModal, setShowModal] = useState(false);
  // ASTT-7319 follow-up: null, was an empty-but-real-shaped object
  // ({LP_CurriculumStatusId: 0, CurriculumStatus: "", isActive: true}).
  // Confirmed via a genuine, reproducible MUI console warning ("None of
  // the options match with ...") -- same root cause as
  // DtrEmailDistro.js's identical fix: an object with id: 0 (a "nothing
  // selected" sentinel, not a real database id) can never match a real
  // option, so isOptionEqualToValue permanently failed to find a match
  // whenever nothing was selected yet. Every usage of this state
  // elsewhere in this file already uses optional chaining or is a setter
  // call, and the effect below only ever assigns a real matched option
  // to it (never resets it), so null simply persists correctly until a
  // genuine match is found.
  const [curriculumStatus, setCurriculumStatus] = useState(null);

  const [curriculumStatusId, setCurriculumStatusId] = useState(0);
  const [activeEditorField, setActiveEditorField] = useState(null);
  // ASTT-7319: reuses the same endpoint already powering the DTR List
  // panel on the Class Roster page (DtrList.js/getDTRListClassRosterIdWise)
  // -- no new endpoint needed. Keyed by "YYYY-MM-DD" -> "Sent"|"Draft" for
  // the calendar's custom day rendering below.
  const [dtrStatusByDate, setDtrStatusByDate] = useState({});

  // ASTT-7319 follow-up: extracted out of the effect below (it used to be
  // a locally-scoped function invoked once per rosterId change) so it can
  // also be called directly after a successful Save/Send -- previously
  // the calendar's Sent/Draft highlighting only ever refreshed when the
  // Class Roster Ids selection itself changed, never after actually
  // saving a new entry, matching the reported "have to refresh and
  // re-select the roster" workaround.
  const loadDtrDatesForCalendar = async () => {
    if (!rosterId) return;
    try {
      const response = await getDTRListClassRosterIdWise(rosterId);
      if (response?.status !== true) return;
      const lookup = {};
      (response?.data ?? []).forEach((row) => {
        if (!row?.DTRDate) return;
        const dateKey = dayjs(row.DTRDate).format("YYYY-MM-DD");
        lookup[dateKey] = row.DTRStatus;
      });
      setDtrStatusByDate(lookup);
    } catch (error) {
      // Non-critical -- the date picker just renders without highlights
      // if this fails, rather than blocking date selection entirely.
      console.error("Failed to load DTR dates for calendar:", error);
    }
  };

  useEffect(() => {
    let cancelled = false;

    const runInitialLoad = async () => {
      if (!rosterId) return;
      try {
        const response = await getDTRListClassRosterIdWise(rosterId);
        if (cancelled || response?.status !== true) return;
        const lookup = {};
        (response?.data ?? []).forEach((row) => {
          if (!row?.DTRDate) return;
          const dateKey = dayjs(row.DTRDate).format("YYYY-MM-DD");
          lookup[dateKey] = row.DTRStatus;
        });
        setDtrStatusByDate(lookup);
      } catch (error) {
        if (!cancelled) {
          console.error("Failed to load DTR dates for calendar:", error);
        }
      }
    };

    runInitialLoad();

    return () => {
      cancelled = true;
    };
  }, [rosterId]);

  const initialEmailDistroValue = {
    LP_DTRDistroId: 0,
    DistroName: "",
    DistroEmail: "",
  };

  const [emailDistroValue, setEmailDistroValue] = useState(
    initialEmailDistroValue,
  );
  const [isDistroEdit, setIsDistroEdit] = useState(false);

  const updateEmailDistro = (value) => {
    setEmailDistroValue(value);
    setIsDistroEdit(false);
  };

  const [classRosterId, setClassRosterId] = useState("");
  const [ShowAIPrompt, setShowAIPrompt] = useState(false);
  const [currentNotes, setCurrentNotes] = useState("");
  const [aiNotes, setAiNotes] = useState("");
  const [fieldLableName, setFieldLableName] = useState("");
  const [fieldMaxLenght, setFieldMaxLenght] = useState(0);

  // State for the deferred-upload attachment flow (nothing uploads until Save):
  //   - dtrId: FK of the loaded/saved entry (0 if never saved).
  //   - existingAttachments: already-saved attachments for this entry (getDTRAttachmentsByParentId).
  //   - pendingImages: inline images pasted/inserted but not yet saved.
  //   - pendingFiles: staged discrete file attachments (owned by DTRAttachmentList, reported
  //     up via onPendingFilesChange).
  const [dtrId, setDtrId] = useState(0);
  const [existingAttachments, setExistingAttachments] = useState([]);
  // ASTT-7319: Preview button/dialog, per direct stakeholder-demo
  // feedback -- visible whenever dtrId > 0 (this entry has been saved at
  // least once, Draft or Sent), matching the request.
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [pendingImages, setPendingImages] = useState([]);
  const [pendingFiles, setPendingFiles] = useState([]);
  // Explicit, deliberately-bumped counter (not derived from existingAttachments, whose
  // filtered array is recreated fresh every render) — see DTRAttachmentList's resetSignal.
  const [pendingFilesResetKey, setPendingFilesResetKey] = useState(0);

  const existingFileAttachments = existingAttachments.filter(
    (a) => a.AttachmentKind === "FileAttachment",
  );

  // Re-filters pendingImages against each field's CURRENT html on every render, so only
  // images still actually referenced get uploaded on Save (a pasted-then-deleted image is
  // dropped rather than orphan-uploaded).
  const currentFieldValuesByName = {
    Successes: successes,
    Challenges: challenges,
    TopicsCovered: topicsCovered,
    ClassRiskNotes: classRiskNotes,
  };
  const referencedPendingImages = pendingImages.filter((p) => {
    const currentValue = currentFieldValuesByName[p.fieldName] || "";
    return currentValue.includes(`pending:${p.placeholderToken}`);
  });

  const fileCount = existingFileAttachments.length + pendingFiles.length;
  const fileSizeBytes =
    existingFileAttachments.reduce((sum, a) => sum + (a.FileSizeBytes || 0), 0) +
    pendingFiles.reduce((sum, p) => sum + (p.file?.size || 0), 0);

  useEffect(() => {
    let cancelled = false;

    const fetchData = async () => {
      try {
        if (!cancelled) {
          setClassRosterId(rosterId);
        }

        // ASTT-7319 follow-up: fetchDTRNotes below (and its
        // setLoadedDtrDate) wrapped in this guard -- it was firing
        // unconditionally even with an empty rosterId on a normal page
        // load before any roster is selected, producing a genuine 400 in
        // the console. getCurriculumStatus() further down is independent
        // of roster selection and deliberately still runs either way, so
        // its dropdown options are ready whenever a roster is selected.
        if (rosterId) {
          const defaultDtrDate = formatDate(
            dtrDate != null ? dtrDate : new Date(),
            "MM-DD-YYYY",
          );

          // classRosterId state update above is async — use the rosterId prop directly,
          // which is already current, to avoid sending a stale/blank value on first mount.
          await fetchDTRNotes(rosterId, defaultDtrDate, true);

          if (!cancelled) {
            setLoadedDtrDate(defaultDate);
          }
        } else if (!cancelled) {
          // ASTT-7319 follow-up: clearing the Class Roster Ids selection
          // wasn't resetting Successes/Challenges/Topics Covered/Parking
          // Lot (or, downstream, Email Distro/existing attachments/read-
          // only state) at all -- this effect's guard above correctly
          // skips fetching for an empty rosterId (that was the actual
          // fix for the earlier 400-error report), but never had a
          // matching "and reset everything to empty" branch for when
          // rosterId goes empty specifically because the user cleared it
          // (as opposed to simply not having selected one yet on a fresh
          // mount, where these fields are already empty regardless).
          // Reuses fetchDTRNotes's own reset call directly rather than
          // duplicating field-by-field logic -- setting
          // trainingNotesDetails back to its initial, all-empty shape
          // cascades correctly through the existing effects: distroId
          // (and, downstream, Email Distro's own value) and dtrId (and,
          // downstream, existingAttachments) all already reset
          // themselves whenever trainingNotesDetails changes, so this is
          // the single, established place to trigger that, not a new
          // parallel reset path.
          setTrainingNotesDetails(initialTrainingNotesDetails);
          // ASTT-7319 follow-up: curriculumStatusId and selectedDtrDate
          // added -- confirmed neither is actually derived from
          // trainingNotesDetails at all (curriculumStatusId is set
          // directly by handleExistingTrainingDetails/
          // handleNoTrainingDetails during a fetch, entirely separately;
          // selectedDtrDate only ever changes via the date picker itself
          // or a successful load), so the reset above never touched
          // either one -- Curriculum Status and the DTR Date picker both
          // kept showing their last-loaded values indefinitely after
          // clearing the roster. 0 here now correctly falls through to
          // the explicit else branch just added to the
          // curriculumStatusId-dependent effect below (no option can
          // ever have LP_CurriculumStatusId === 0), and defaultDate
          // matches this field's own initial state.
          setCurriculumStatusId(0);
          onSelectedDtrDateChange(defaultDate);
        }

        if (cancelled) return;

        const curriculumStatus = await getCurriculumStatus();

        if (cancelled) return;

        if (curriculumStatus?.message === "Success") {
          if (!cancelled) {
            setCurriculumStatusOptions(curriculumStatus?.data);
          }
        }
      } catch (error) {
        if (!cancelled) {
          console.error("Failed to fetch data:", error);
        }
      }
    };

    fetchData();

    return () => {
      cancelled = true;
    };
  }, [rosterId]);
  useEffect(() => {
    const curriculumStatusValue = curriculumStatusOptions?.find(
      (option) => option?.LP_CurriculumStatusId === curriculumStatusId,
    );

    // ASTT-7319 follow-up: explicit else added -- this only ever assigned
    // a matched option, silently doing nothing otherwise. That was fine
    // for this state's own initial null (nothing to reset from yet), but
    // meant curriculumStatus could never actually clear back to null
    // once it had held a real value and curriculumStatusId later changed
    // to something with no matching option (e.g. reset to 0 when the
    // Class Roster Ids selection is cleared) -- it just kept showing
    // whatever was last matched, indefinitely.
    if (curriculumStatusValue !== undefined) {
      setCurriculumStatus(curriculumStatusValue);
    } else {
      setCurriculumStatus(null);
    }
  }, [curriculumStatusId, curriculumStatusOptions]);

  const getRosterIdString = (rosterId) => {
    return Array.isArray(rosterId)
      ? rosterId.map((roster) => String(roster.rosterId)).join(",")
      : String(rosterId);
  };

  // Fetches existing attachments whenever dtrId changes (Load-ing a different date resolves
  // a different — or no — entry). A successful save also sets existingAttachments directly
  // from that save's response, since saving an already-existing entry doesn't change dtrId.
  useEffect(() => {
    let cancelled = false;

    const fetchExistingAttachments = async () => {
      if (!dtrId) {
        if (!cancelled) setExistingAttachments([]);
        return;
      }
      const response = await getDTRAttachmentsByParentId(dtrId, null);
      if (cancelled) return;
      if (response?.statusCode === 200 && Array.isArray(response.data)) {
        setExistingAttachments(response.data);
      } else if (response?.statusCode === 404) {
        setExistingAttachments([]);
      } else {
        // Real failure, not "no attachments" — surface it explicitly.
        setExistingAttachments([]);
        showSnack(
          "Could not load existing attachments for this entry. They may still exist — please refresh or contact support if this persists.",
          "error",
        );
      }
    };

    fetchExistingAttachments();
    return () => {
      cancelled = true;
    };
  }, [dtrId]);

  // ASTT-7319 follow-up: the saved-image rehydration that used to live
  // here was WRONG -- it called setSuccesses/setChallenges/etc. directly,
  // which meant the very next Save/Send resubmitted that transient blob:
  // URL as the new saved content, permanently overwriting the real
  // SharePoint URL in the database and silently dropping the image from
  // outgoing emails (confirmed via testing: image displayed correctly in
  // the UI after this "fix," but was missing from the actual sent
  // email). Moved into RichTextField itself (RichTextEditorField.js),
  // which already has the correct pattern for exactly this kind of
  // display-only transformation (see its own rehydratePendingImageSrcs/
  // displayValue) -- existingAttachments/downloadAttachmentImageAsBlobUrl
  // are now passed down as props instead, so successes/challenges/etc.
  // here always keep the real, permanent SharePoint URL.

  // Attachment counts/sizes for the
  // summary bar are computed directly above (existingAttachments + pendingImages +
  // pendingFiles) rather than via a callback from DTRAttachmentList, since this screen now
  // owns existingAttachments itself.
  // Tracks whether there's anything unsaved (image/file staged, or text edited) so we can
  // warn before the user navigates away and silently loses it.
  const isDirtyRef = useRef(false);

  const markDirty = () => {
    if (!isDirtyRef.current) {
      isDirtyRef.current = true;
      onDirtyChange?.(true);
    }
  };

  // Keeps the parent tab container's "is this tab dirty" in sync when unsaved changes are
  // legitimately cleared (successful save, or a deliberate Load of a different date).
  const clearDirty = () => {
    isDirtyRef.current = false;
    onDirtyChange?.(false);
  };

  // Fired when an image is pasted/inserted (blob: preview already shown) — stages the raw
  // File until Save; nothing is uploaded here.
  const handleImagePending = (file, meta) => {
    markDirty();
    setPendingImages((prev) => [
      ...prev,
      {
        file,
        placeholderToken: meta.placeholderToken,
        fieldName: meta.fieldName,
        kind: "InlineImage",
      },
    ]);
  };

  const handlePendingFilesChange = (files) => {
    setPendingFiles(files);
    if (files.length > 0) markDirty();
  };

  const handleExistingAttachmentRemoved = (attachmentId) => {
    setExistingAttachments((prev) =>
      prev.filter((a) => a.LP_DTRAttachmentId !== attachmentId),
    );
  };

  // Warn on browser close/refresh/tab-close if there's anything unsaved.
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (!isDirtyRef.current) return;
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, []);

  // Covers in-app navigation away from this page (sidebar/link/back-forward) — complements
  // the tab-switch guard in DtrDetails.js. Requires a data router (see App.js).
  const navigationBlocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      isDirtyRef.current &&
      currentLocation.pathname !== nextLocation.pathname,
  );

  // Helper function to check if training details exist
  const hasTrainingDetails = (trainingDetailsResponse) => {
    return trainingDetailsResponse?.LP_DTRTrainingNotesId !== 0;
  };

  // Helper function to check if curriculum status exists
  const hasCurriculumStatus = (trainingDetailsResponse) => {
    return trainingDetailsResponse?.LP_CurriculumStatusId !== 0;
  };

  // Helper function to handle no training details case
  const handleNoTrainingDetails = (
    trainingDetailsResponse,
    isOnPageLoad,
    setCurriculumStatusId,
    showSnack,
  ) => {
    if (hasCurriculumStatus(trainingDetailsResponse)) {
      setCurriculumStatusId(trainingDetailsResponse.LP_CurriculumStatusId);
    }

    if (!isOnPageLoad) {
      showSnack("No training Details for the selected date.", "info");
    }
  };

  // Helper function to handle existing training details
  const handleExistingTrainingDetails = (
    trainingDetailsResponse,
    setCurriculumStatusId,
    setTrainingNotesDetails,
  ) => {
    setCurriculumStatusId(trainingDetailsResponse.LP_CurriculumStatusId);
    setTrainingNotesDetails(trainingDetailsResponse);
  };

  // Helper function to handle email distro on page load
  const handleEmailDistroOnLoad = (
    trainingDetailsResponse,
    isOnPageLoad,
    updateEmailDistro,
    setDistroId,
  ) => {
    // ASTT-7319 follow-up: isOnPageLoad condition removed -- this only
    // ever ran on the very first mount, never on a Load-button click or
    // jump-to-DTR, which is the actual bug being fixed. Also was passing
    // a plain string (trainingDetailsResponse.EmailDistro) straight to
    // updateEmailDistro, which just calls setEmailDistroValue(value) --
    // Autocomplete's getOptionLabel reads option?.DistroName, which a
    // plain string doesn't have, so this alone would never have rendered
    // correctly even when it did run. Wrapped in a proper object now,
    // matching HandOffReport.js's own working pattern for the same field.
    if (trainingDetailsResponse?.EmailDistro) {
      updateEmailDistro({
        LP_DTRDistroId: trainingDetailsResponse.EmailDistroId || 0,
        DistroName: trainingDetailsResponse.EmailDistro,
        DistroEmail: trainingDetailsResponse.EmailDistro,
      });
      setDistroId(trainingDetailsResponse.EmailDistroId || 0);
    }
  };

  const handleEditorBlur = () => {
    setActiveEditorField(null);
  };

  // Rejects (rather than just flags) any edit that would push a field's
  // plain-text length past maxLength: bumping the revert key remounts the
  // Editor (via RichTextField's editorKey prop) back to the last accepted
  // value, discarding the offending keystroke/paste. Matches the pattern
  // already used in HandOffReport.js. Also surfaces a warning via the
  // existing snackbar so the rejection isn't silent.
  const createBoundedChangeHandler =
    (setValue, maxLength, bumpRevertKey, fieldLabel) => (e) => {
      const newValue = e.htmlValue ?? "";
      const plainLength = stripHtml(newValue).length;

      if (plainLength > maxLength) {
        bumpRevertKey((k) => k + 1);
        showSnack(
          `${fieldLabel} has reached the ${maxLength}-character limit.`,
          "warning",
        );
        return;
      }
      markDirty();
      setValue(newValue);
    };

  // Helper function to process successful response
  const processSuccessfulResponse = (
    trainingDetailsResponse,
    isOnPageLoad,
    setCurriculumStatusId,
    setTrainingNotesDetails,
    showSnack,
    updateEmailDistro,
    setDistroId,
  ) => {
    if (!hasTrainingDetails(trainingDetailsResponse)) {
      handleNoTrainingDetails(
        trainingDetailsResponse,
        isOnPageLoad,
        setCurriculumStatusId,
        showSnack,
      );
    } else {
      handleExistingTrainingDetails(
        trainingDetailsResponse,
        setCurriculumStatusId,
        setTrainingNotesDetails,
      );
    }

    handleEmailDistroOnLoad(
      trainingDetailsResponse,
      isOnPageLoad,
      updateEmailDistro,
      setDistroId,
    );
  };

  // Helper function to handle 404 response
  const handle404Response = (isOnPageLoad, showSnack) => {
    if (!isOnPageLoad) {
      showSnack("No training Details for the selected date.", "info");
    }
  };

  // Refactored main function
  const fetchDTRNotes = async (rosterId, dtrDate, isOnPageLoad) => {
    setLoading(true);
    setTrainingNotesDetails(initialTrainingNotesDetails);

    const rosterIdString = getRosterIdString(rosterId);
    const response = await getDTRTrainingNotes(rosterIdString, dtrDate);

    if (response?.statusCode === 200) {
      const trainingDetailsResponse = response?.data[0];
      processSuccessfulResponse(
        trainingDetailsResponse,
        isOnPageLoad,
        setCurriculumStatusId,
        setTrainingNotesDetails,
        showSnack,
        updateEmailDistro,
        setDistroId,
      );
    } else if (response?.statusCode === 404) {
      handle404Response(isOnPageLoad, showSnack);
    }

    setLoading(false);
  };

  const prepareSaveAsDraftPayload = () => {
    let payloads = [];
    if (rosterId) {
      const classRosterIds = getRosterIdString(rosterId);
      payloads = {
        classRosterId: classRosterIds,
        classRequestId,
        dtrDate: selectedDtrDate,
        successes: sanitizeEditorHtml(successes),
        challenges: sanitizeEditorHtml(challenges),
        topicsCovered: sanitizeEditorHtml(topicsCovered),
        classRiskCategory: curriculumStatus?.CurriculumStatus,
        classRiskNotes: sanitizeEditorHtml(classRiskNotes),
        lP_DTRDistroId: emailDistroValue?.LP_DTRDistroId,
        emailDistro: emailDistroValue?.DistroEmail,
        dtrStatus: "Draft",
        modifiedBy: localStorage.getItem("User"),
        attachmentIdsToDelete: "",
      };
    }
    return payloads;
  };

  const prepareMailPayload = (savedDtrId) => {
    const payload = {
      dtrId: savedDtrId,
      distroId: emailDistroValue?.LP_DTRDistroId,
      emailCc: localStorage.getItem("EmailId"),
      emailSubject: `Daily training report has been submitted by ${localStorage.getItem(
        "User",
      )}`,
    };
    return payload;
  };

  const validateForEmptyFields = () => {
    if (!rosterId) {
      showSnack("Roster ID is missing. Please select a roster.", "error");
      return false;
    } else if (isEmptyString(stripHtml(successes))) {
      showSnack("Cannot save empty spaces for successes.", "warning");
      return false;
    } else if (isEmptyString(stripHtml(challenges))) {
      showSnack("Cannot save empty spaces for challenges.", "warning");
      return false;
    } else if (isEmptyString(stripHtml(topicsCovered))) {
      showSnack("Cannot save empty spaces for topics covered.", "warning");
      return false;
    } else if (isEmptyString(stripHtml(classRiskNotes))) {
      showSnack("Cannot save empty spaces for parking lot notes.", "warning");
      return false;
    }
    return true;
  };

  // A pending image pasted and then deleted before Save is simply never uploaded — nothing
  // to clean up server-side, since nothing was sent there.
  const saveAsDraft = async () => {
    const payload = prepareSaveAsDraftPayload();
    const combinedPendingAttachments = [...referencedPendingImages, ...pendingFiles];

    const response = await saveDTRTrainingNotesWithAttachments(
      payload,
      combinedPendingAttachments,
    );

    if (response?.statusCode === 200) {
      const savedDtrId = response?.data?.dtrId || 0;
      setDtrId(savedDtrId);
      // Authoritative — replaces existingAttachments with exactly what the server confirms.
      if (Array.isArray(response?.data?.attachments)) {
        setExistingAttachments(response.data.attachments);
      }

      // ASTT-7319 follow-up: reordered -- setPendingImages([])/
      // setActiveEditorField(null) used to run BEFORE this refetch, not
      // after. That was a genuine race: clearing pendingImages removes
      // the only thing rehydratePendingImageSrcs (in
      // RichTextEditorField.js) can use to rebuild a live blob: URL for
      // display, and deactivating the field switches it to the preview,
      // which renders raw HTML directly rather than through Quill. In the
      // window between that clear/deactivate and this await actually
      // resolving, the field's value still held the stale
      // "pending:{token}" markers with nothing left to rehydrate them --
      // exactly the broken-image/net::ERR_UNKNOWN_URL_SCHEME symptom
      // reported, confirmed reproducible on every save that included a
      // new inline image. Running the refetch first means
      // successes/challenges/etc. already hold the real, corrected URLs
      // from the server by the time pendingImages is cleared, so there's
      // no gap where a stale marker exists with nothing able to resolve it.
      if (referencedPendingImages.length > 0) {
        await fetchDTRNotes(rosterId, selectedDtrDate, false);
      }

      setActiveEditorField(null);
      setPendingImages([]);
      setPendingFiles([]);
      setPendingFilesResetKey((k) => k + 1);

      clearDirty();
      return savedDtrId;
    }
    return 0;
  };

  const handleSaveAsDraft = async () => {
    setLoading(true);
    if (validateForEmptyFields()) {
      setLoadingMessage(
        referencedPendingImages.length > 0 || pendingFiles.length > 0
          ? "Saving content and uploading attachments..."
          : "Saving content...",
      );
      const result = await saveAsDraft();
      if (result) {
        showSnack("Training notes saved successfully.", "success");
        // ASTT-7319 follow-up: calendar highlighting + parent-owned badge/
        // roster-details refresh, both previously only updated on the
        // next Class Roster Ids selection change, never right after an
        // actual save.
        loadDtrDatesForCalendar();
        onDtrSaved?.();
      } else {
        showSnack("Internal error occured.", "error");
      }
    }
    setLoading(false);
    setLoadingMessage("");
  };

  const handleSendEmail = async () => {
    // ASTT-7319 follow-up: optional chaining added -- emailDistroValue is
    // null now (was an empty-but-real-shaped object), matching
    // DtrEmailDistro.js's own fix propagating outward through the
    // updateEmailDistro callback. A direct access here crashed the
    // moment DtrEmailDistro.js's internal effect first called
    // updateEmailDistro(null) on mount, which is exactly the render
    // crash this fixes -- ?? "" treats null the same as "" here, which
    // is the correct, intended meaning ("nothing selected").
    if ((emailDistroValue?.DistroEmail ?? "") === "") {
      showSnack("Please select email distro.", "error");
      return;
    }
    if (!validateForEmptyFields()) {
      return;
    }
    setLoading(true);
    try {
      setLoadingMessage(
        referencedPendingImages.length > 0 || pendingFiles.length > 0
          ? "Saving content and uploading attachments..."
          : "Saving content...",
      );
      const savedDtrId = await saveAsDraft();
      if (savedDtrId > 0) {
        setLoadingMessage("Sending email...");
        const payload = prepareMailPayload(savedDtrId);
        const emailResponse = await sendDTREmail(payload);
        if (emailResponse?.statusCode === 200) {
          showSnack("Email sent successfully.", "success");
          // ASTT-7319 follow-up: was building defaultDtrDate from the
          // dtrDate prop (a one-time initial value from redirect/route
          // state, essentially always "today" for a normal page load)
          // instead of selectedDtrDate -- the date the user was actually
          // on and just sent. Confirmed as the reported bug: after a
          // successful send for any non-today date, the UI jumped back
          // to today's Training Notes instead of staying on and
          // reflecting the entry that was just sent as read-only.
          // selectedDtrDate matches what every other refetch call in
          // this file already correctly uses (saveAsDraft's own refetch
          // above, handleLoadTrainingDetails) -- this was the one
          // outlier still building its own, wrong date.
          fetchDTRNotes(rosterId, selectedDtrDate, false);
          // ASTT-7319 follow-up: same refresh as handleSaveAsDraft's
          // identical fix -- Send as Email saves a draft first
          // (saveAsDraft above), then emails it, so this needs the exact
          // same calendar/badge/roster-details refresh.
          loadDtrDatesForCalendar();
          onDtrSaved?.();
        } else {
          showSnack("Internal error occured.", "error");
        }
      } else {
        showSnack("Internal error occured.", "error");
      }
    } catch (error) {
      console.error("error occured:", error);
    } finally {
      setLoading(false);
      setLoadingMessage("");
    }
  };

  const checkIfIsReadOnly = () => {
    if (trainingNotesDetails.DTRStatus === "Sent") {
      setIsReadOnly(true);
    } else setIsReadOnly(false);
  };

  useEffect(() => {
    setSuccesses(trainingNotesDetails.Successes);
    setChallenges(trainingNotesDetails.Challenges);
    setTopicsCovered(trainingNotesDetails.TopicsCovered);
    setClassRiskNotes(trainingNotesDetails.ClassRiskNotes);
    // ASTT-7319 follow-up: Email Distro wasn't pre-filling on reload
    // (Load button/jump-to-DTR), confirmed working in HandOffReport.js.
    // Root cause: two separate mechanisms exist here -- an older,
    // isOnPageLoad-gated one (handleEmailDistroOnLoad, using
    // EmailDistroId/EmailDistro) that only ever ran on the very first
    // mount, and this unconditional effect (using LP_DTRDistroId), which
    // runs on every load but fires AFTER handleEmailDistroOnLoad's own
    // setDistroId call (regular async function calls complete before an
    // effect with a changed dependency fires), meaning whichever field
    // name this actually is would win -- overriding the correct value
    // with undefined if LP_DTRDistroId isn't genuinely returned by the
    // backend. Rather than guess which field name is the real one
    // without visibility into the stored procedure's actual response
    // shape, falling back to EmailDistroId covers either case.
    setDistroId(trainingNotesDetails.LP_DTRDistroId || trainingNotesDetails.EmailDistroId || 0);
    setDtrId(trainingNotesDetails.LP_DTRTrainingNotesId || 0);
    checkIfIsReadOnly();
  }, [trainingNotesDetails]);

  // ASTT-7319: MUI's DatePicker onChange passes the new value directly (a
  // dayjs object or null), not a native event -- adapted here so
  // selectedDtrDate keeps its existing "YYYY-MM-DD" string type/format
  // throughout the rest of this file, with nothing else needing to change.
  const onSelectDateChange = (newValue) => {
    if (!newValue) return;
    const dateValue = newValue.toDate();
    const today = new Date();
    if (dateValue > today) {
      return false;
    }
    onSelectedDtrDateChange(newValue.format("YYYY-MM-DD"));
  };

  const handleLoadTrainingDetails = () => {
    // ASTT-7319: was selectDateRef.current.value -- a direct DOM read that
    // assumed a native <input> existed at that ref. Now reads
    // selectedDtrDate state instead, which is state-driven and works
    // regardless of which component renders the picker; selectedDtrDate
    // is already kept in sync by onSelectDateChange on every change.
    if (!selectedDtrDate) {
      showSnack("Please select a date to load.", "warning");
    } else {
      fetchDTRNotes(rosterId, selectedDtrDate, false);
      setLoadedDtrDate(selectedDtrDate);
      // Deactivate the open field so it picks up the newly-loaded content on next click
      // (RichTextEditorField.js freezes its live value once activated).
      setActiveEditorField(null);
      setPendingImages([]);
      setPendingFiles([]);
      setPendingFilesResetKey((k) => k + 1);
      clearDirty();
    }
  };

  // ASTT-7319: "View DTRs" cross-component jump (item 7) -- mirrors
  // handleLoadTrainingDetails above exactly (same fetch, same
  // loadedDtrDate/activeEditorField/pendingImages/pendingFiles reset,
  // same clearDirty), except triggered by jumpToDtrDate changing (a
  // {date, nonce} object from DtrDetails.js) rather than the Load button
  // click, and the date itself comes from the prop, not selectedDtrDate
  // state -- so it's read directly here rather than requiring a separate
  // click after setSelectedDtrDate would have applied.
  //
  // ASTT-7319 follow-up: jumpToDtrDate.date is now the RAW backend value
  // (ClassRosterDetailsTable.js's handleViewDtrs no longer pre-formats it,
  // since ViewDtrsDialog.js needs real dayjs comparisons for its own
  // filter/sort -- formatted "MM/DD/YYYY" strings sort incorrectly across
  // year boundaries). Parsed here via plain dayjs(value) -- reliable
  // without any plugin, since the raw value is an ISO-ish datetime string,
  // dayjs's native strength -- rather than the earlier "MM/DD/YYYY" split,
  // which is no longer the format arriving here.
  useEffect(() => {
    if (!jumpToDtrDate?.date) return;
    const parsed = dayjs(jumpToDtrDate.date);
    if (!parsed.isValid()) return;
    const newDate = parsed.format("YYYY-MM-DD");

    onSelectedDtrDateChange(newDate);
    fetchDTRNotes(rosterId, newDate, false);
    setLoadedDtrDate(newDate);
    setActiveEditorField(null);
    setPendingImages([]);
    setPendingFiles([]);
    setPendingFilesResetKey((k) => k + 1);
    clearDirty();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jumpToDtrDate]);

  const handleCurriculumStatusChange = (event, value) => {
    if (value) {
      setCurriculumStatus(value);
    }
  };

  const handleDistroAddorEdit = (isEdit) => {
    setIsDistroEdit(isEdit);
    setShowModal(true);
  };

  // Single configuration object
  const FIELD_HANDLERS = {
    Successes: {
      getValue: () => successes,
      setValue: setSuccesses,
      maxLength: MAX_LENGTH,
    },
    challenges: {
      getValue: () => challenges,
      setValue: setChallenges,
      maxLength: MAX_LENGTH,
    },
    topicsCovered: {
      getValue: () => topicsCovered,
      setValue: setTopicsCovered,
      maxLength: MAX_LENGTH,
    },
    classRiskNotes: {
      getValue: () => classRiskNotes,
      setValue: setClassRiskNotes,
      maxLength: MAX_LENGTH,
    },
  };

  const openAIPrompt = (fieldName) => {
    const handler = FIELD_HANDLERS[fieldName];
    if (!handler) return;

    setCurrentNotes("");
    setFieldLableName(fieldName);
    setCurrentNotes(stripHtml(handler.getValue())); // strip HTML before passing to AIPrompt
    setFieldMaxLenght(handler.maxLength);
    setShowAIPrompt(true);
  };
  const handleClose = (acceptanceFlag) => {
    setShowAIPrompt(false);

    if (acceptanceFlag) {
      const handler = FIELD_HANDLERS[fieldLableName];
      if (handler) {
        handler.setValue(aiNotes);
      }
    }
  };

  return (
    <ThemeProvider theme={theme}>
      {loading ? <BackdropComponent message={loadingMessage} /> : null}
      {ShowAIPrompt && (
        <AIPrompt
          promptOpen={ShowAIPrompt}
          handleClose={handleClose}
          currentNotes={currentNotes}
          setAiNotes={setAiNotes}
          fieldMaxLenght={fieldMaxLenght}
        />
      )}

      {/* ASTT-7319 follow-up: restructured to two md={6} halves, matching
          the Successes/Challenges 50/50 split below exactly -- Curriculum
          Status previously started at x=7.5 (after DTR Date 2.5 + Load 1
          + Last Modified 4), not lined up with "Challenges and Action
          Taken" at x=6. Last Modified moved out entirely to a footer
          section (see near the bottom of this file) -- removing it here
          is what actually makes this clean 6/6 split possible. */}
      <Grid container id="trainingNotesDetails">
        <Grid item xs={12} sm={12} md={6} sx={{ px: 1 }}>
          <FieldLabel>DTR Date</FieldLabel>
          {/* ASTT-7319 follow-up: DTR Date and Load packed into one flex
              row with a small gap, was two separate Grid items with
              independent padding creating visible space between them
              -- matches the same flex pattern already used for Email
              Distro + Add/Update. */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {/* ASTT-7319: replaced the native <input type="date"> with
                MUI's actual DatePicker -- a native date input's popup
                calendar is rendered entirely by the browser and cannot
                be customized to highlight specific dates, for any
                framework. renderDay below colors each day green (Sent)
                or amber (Draft) using dtrStatusByDate, reusing the exact
                same data DtrList.js already fetches for the Class
                Roster page's DTR List panel. */}
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                value={selectedDtrDate ? dayjs(selectedDtrDate) : null}
                onChange={onSelectDateChange}
                maxDate={dayjs(todayDate)}
                // ASTT-7319 follow-up: popup calendar was docking center,
                // overlapping the sidebar -- bottom-end anchors it to the
                // right/button side of the input instead.
                PopperProps={{ placement: "bottom-end" }}
                // ASTT-7319 follow-up: legend moved from above the input
                // into the calendar popup itself, at the bottom, per
                // direct request -- @mui/x-date-pickers v5's ActionBar
                // override is the documented slot for this; a plain
                // DatePicker configures no Cancel/OK actions by default,
                // so this component renders only the legend, nothing
                // else needs preserving alongside it.
                components={{ ActionBar: DateLegendActionBar }}
                renderDay={(day, _selectedDays, pickersDayProps) => {
                  const dateKey = day.format("YYYY-MM-DD");
                  const status = dtrStatusByDate[dateKey];
                  let highlightSx = {};
                  if (status === "Sent") {
                    highlightSx = {
                      backgroundColor: "#DCF5E0",
                      border: "1px solid #2AA154",
                      "&:hover": { backgroundColor: "#c5edcb" },
                    };
                  } else if (status === "Draft") {
                    highlightSx = {
                      backgroundColor: "#FFF4D6",
                      border: "1px solid #E0A800",
                      "&:hover": { backgroundColor: "#ffe9ad" },
                    };
                  }
                  // ASTT-7319 follow-up: key extracted and passed
                  // directly, rest spread separately -- pickersDayProps
                  // (supplied by MUI's renderDay callback) already
                  // includes a key internally; spreading it as part of
                  // {...pickersDayProps} is exactly the "key prop being
                  // spread into JSX" anti-pattern React/MUI both warn
                  // about, since React requires key to be a literal JSX
                  // attribute, never a spread-in property.
                  const { key, ...otherDayProps } = pickersDayProps;
                  return (
                    <PickersDay
                      key={key}
                      {...otherDayProps}
                      sx={highlightSx}
                    />
                  );
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    sx={{
                      minWidth: 160,
                      "& .MuiOutlinedInput-root": {
                        // ASTT-7319 follow-up: was 32 (pill-style),
                        // matches RichTextEditorField.js's actual radius
                        // now, per direct request.
                        borderRadius: "4px",
                        border: "1px solid #d3d3d3",
                        "& fieldset": {
                          border: "none",
                        },
                      },
                    }}
                  />
                )}
              />
            </LocalizationProvider>
            <CustomButton variant="contained" onClick={handleLoadTrainingDetails}>
              Load
            </CustomButton>
          </Box>
        </Grid>
        <Grid item xs={12} sm={12} md={6} sx={{ px: 1 }}>
          <FieldLabel>Curriculum Status</FieldLabel>
          <Autocomplete
            disablePortal
            size="small"
            value={curriculumStatus}
            options={curriculumStatusOptions}
            isOptionEqualToValue={(option, value) =>
              option?.LP_CurriculumStatusId === value?.LP_CurriculumStatusId
            }
            onChange={(event, value) =>
              handleCurriculumStatusChange(event, value)
            }
            sx={{
              width: 200,
              opacity: 1,
              // ASTT-7319 follow-up: borderWidth/borderStyle/borderColor
              // removed -- this was a genuine double border, stacked with
              // renderInput's own TextField border below (confirmed via
              // direct request to remove the shadow/border look). Only
              // the inner TextField's single border remains now.
              borderRadius: "4px",
            }}
            getOptionLabel={(option) => option?.CurriculumStatus || ""}
            renderInput={(params) => (
              <TextField
                {...params}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "4px",
                    border: "1px solid #d3d3d3",
                    "& fieldset": {
                      border: "none",
                    },
                  },
                }}
              />
            )}
            disabled={isReadOnly}
          />
        </Grid>
      </Grid>
        <Grid container>
          {/* ASTT-7319: sm={12} md={6} added -- these paired rich-text
              fields previously used a single fixed xs={6}, meaning the
              exact same half-width column applied at every viewport size
              including phone widths, cramming each editor into an
              unusably narrow half-screen column rather than stacking to
              full width below the medium breakpoint. */}
          <Grid item xs={12} sm={12} md={6} className={classes.padding1Rem}>
            <RichTextField
              label="Successes"
              value={successes}
              editorKey={successesRevertKey}
              onChange={createBoundedChangeHandler(
                setSuccesses,
                MAX_LENGTH,
                setSuccessesRevertKey,
                "Successes",
              )}
              isActive={activeEditorField === "Successes"}
              onActivate={() => setActiveEditorField("Successes")}
              onDeactivate={handleEditorBlur}
              isReadOnly={isReadOnly}
              charCount={stripHtml(successes).length}
              maxLength={MAX_LENGTH}
              onAIClick={() => openAIPrompt("Successes")}
              aiColor="#3299FF"
              fieldName="Successes"
              onImagePending={handleImagePending}
              pendingImages={pendingImages}
              existingAttachments={existingAttachments}
              downloadImageAsBlobUrl={downloadAttachmentImageAsBlobUrl}
            />
          </Grid>
          <Grid item xs={12} sm={12} md={6} className={classes.padding1Rem}>
            <RichTextField
              label="Challenges and Action Taken"
              value={challenges}
              editorKey={challengesRevertKey}
              onChange={createBoundedChangeHandler(
                setChallenges,
                MAX_LENGTH,
                setChallengesRevertKey,
                "Challenges and Action Taken",
              )}
              isActive={activeEditorField === "Challenges"}
              onActivate={() => setActiveEditorField("Challenges")}
              onDeactivate={handleEditorBlur}
              isReadOnly={isReadOnly}
              charCount={stripHtml(challenges).length}
              maxLength={MAX_LENGTH}
              onAIClick={() => openAIPrompt("challenges")}
              aiColor="#29a1c6"
              fieldName="Challenges"
              onImagePending={handleImagePending}
              pendingImages={pendingImages}
              existingAttachments={existingAttachments}
              downloadImageAsBlobUrl={downloadAttachmentImageAsBlobUrl}
            />
          </Grid>
        </Grid>
        <Grid container>
          <Grid item xs={12} sm={12} md={6} className={classes.padding1Rem}>
            <RichTextField
              label="Topics Covered"
              value={topicsCovered}
              editorKey={topicsCoveredRevertKey}
              onChange={createBoundedChangeHandler(
                setTopicsCovered,
                MAX_LENGTH,
                setTopicsCoveredRevertKey,
                "Topics Covered",
              )}
              isActive={activeEditorField === "TopicsCovered"}
              onActivate={() => setActiveEditorField("TopicsCovered")}
              onDeactivate={handleEditorBlur}
              isReadOnly={isReadOnly}
              charCount={stripHtml(topicsCovered).length}
              maxLength={MAX_LENGTH}
              onAIClick={() => openAIPrompt("topicsCovered")}
              aiColor="#29a1c6"
              fieldName="TopicsCovered"
              onImagePending={handleImagePending}
              pendingImages={pendingImages}
              existingAttachments={existingAttachments}
              downloadImageAsBlobUrl={downloadAttachmentImageAsBlobUrl}
            />
          </Grid>
          <Grid item xs={12} sm={12} md={6} className={classes.padding1Rem}>
            <RichTextField
              label="Parking Lot"
              value={classRiskNotes}
              editorKey={classRiskNotesRevertKey}
              onChange={createBoundedChangeHandler(
                setClassRiskNotes,
                MAX_LENGTH,
                setClassRiskNotesRevertKey,
                "Parking Lot",
              )}
              isActive={activeEditorField === "ParkingLot"}
              onActivate={() => setActiveEditorField("ParkingLot")}
              onDeactivate={handleEditorBlur}
              isReadOnly={isReadOnly}
              charCount={stripHtml(classRiskNotes).length}
              maxLength={MAX_LENGTH}
              onAIClick={() => openAIPrompt("classRiskNotes")}
              aiColor="#29a1c6"
              fieldName="ClassRiskNotes"
              onImagePending={handleImagePending}
              pendingImages={pendingImages}
              existingAttachments={existingAttachments}
              downloadImageAsBlobUrl={downloadAttachmentImageAsBlobUrl}
            />
          </Grid>
        </Grid>
        {/* ASTT-7319: Email Distro (with its Add/Update button) paired
            with Attachments in one row, matching the Topics Covered/
            Parking Lot sm={12} md={6} pattern -- was two entirely
            separate rows (Email Distro living in the sticky action bar,
            Attachments alone here). Attachment control's summary moved
            below the control itself (was above); control's own width now
            fills its column instead of a fixed 260px, since it shares a
            half-width column with Email Distro now, not a full-width row
            by itself. */}
        <Grid container>
          <Grid item xs={12} sm={12} md={6} className={classes.padding1Rem}>
            {/* ASTT-7319: no external FieldLabel here -- DtrEmailDistro.js
                already renders its own "Email Distro" FieldLabel
                internally (fixed earlier this same pass). An external one
                here would have duplicated it directly on top. */}
            <Box sx={{ display: "flex", alignItems: "flex-end", gap: 1 }}>
              {/* ASTT-7319 follow-up: was flexGrow: 1 (filled all
                  remaining space in this flex row) -- maxWidth caps it
                  to a fixed, reduced width per direct request. */}
              {/* ASTT-7319 follow-up: 220 read as too narrow, almost
                  zeroed out, per direct feedback -- width: "fit-content"
                  with a reasonable minWidth sizes it to its actual
                  content instead of an arbitrary fixed number. */}
              {/* ASTT-7319 follow-up: minWidth doubled (260 -> 520), per
                  direct request, after confirming 260 was still too
                  narrow via testing. */}
              <Box sx={{ width: "fit-content", minWidth: 520 }}>
                <DtrEmailDistro
                  open={showModal}
                  isUpdate={isDistroEdit}
                  onClose={() => setShowModal(false)}
                  updateEmailDistro={updateEmailDistro}
                  distroId={distroId}
                  disabled={isReadOnly}
                ></DtrEmailDistro>
              </Box>
              {/* ASTT-7319: implements the UX suggestion from the
                  previous thread -- this used the same bold, filled
                  CustomButton style as the Send/Save split-button below,
                  two equally-prominent "primary" actions on one screen
                  doing very different things. size="small" + outlined
                  demotes it, reserving the bold treatment for Send/Save
                  alone. */}
              {/* ASTT-7319 follow-up: optional chaining + ?? "" added --
                  this is the exact line that crashed the whole page
                  (emailDistroValue is null now, was an empty-but-real-
                  shaped object). Same reasoning as handleSendEmail's
                  identical fix above. */}
              {(emailDistroValue?.DistroName ?? "") === "" ? (
                <CustomOutlinedButton
                  variant="outlined"
                  size="small"
                  disabled={isReadOnly}
                  onClick={() => handleDistroAddorEdit(false)}
                >
                  Add
                </CustomOutlinedButton>
              ) : (
                <CustomOutlinedButton
                  variant="outlined"
                  size="small"
                  disabled={isReadOnly}
                  onClick={() => handleDistroAddorEdit(true)}
                >
                  Update
                </CustomOutlinedButton>
              )}
            </Box>
          </Grid>
          <Grid item xs={12} sm={12} md={6} className={classes.padding1Rem}>
            <FieldLabel>Attachments</FieldLabel>
            {/* ASTT-7319: Summary now renders above the drop-zone control,
                was below it; mb: 1.5 gives vertical padding between them,
                per direct request (previously none). */}
            <Box sx={{ mb: 1.5 }}>
              <DTRAttachmentSummary
                fileCount={fileCount}
                fileSizeBytes={fileSizeBytes}
              />
            </Box>
            <DTRAttachmentList
              existingAttachments={existingFileAttachments}
              isReadOnly={isReadOnly}
              onPendingFilesChange={handlePendingFilesChange}
              onExistingRemoved={handleExistingAttachmentRemoved}
              resetSignal={pendingFilesResetKey}
            />
          </Grid>
        </Grid>
        {/* ASTT-7319: sticky so the Send/Save action stays reachable
            regardless of how tall the tab's content is, rather than
            requiring a scroll to the very bottom every time. */}
        <Grid
          container
          sx={{
            position: "sticky",
            bottom: 0,
            backgroundColor: "#fff",
            zIndex: 2,
            // ASTT-7319 follow-up: borderTop removed, per direct request.
          }}
        >
          {/* ASTT-7319: Email Distro + Add/Update button moved out of
              this sticky row entirely -- now paired with Attachments in
              their own row above (see below). */}
          {/* ASTT-7319 follow-up: this row now also holds the Last
              Updated/Last Sent footer info (was a separate row below,
              not sharing the row with Send/Save as requested) -- split
              into xs={8} for the footer info and xs={4} for the button,
              instead of the button alone taking the full xs={12}. */}
          <Grid item xs={8} sx={{ px: 1 }}>
            <Grid container>
              {trainingNotesDetails?.ModifiedBy && (
                <>
                  <Grid item xs={3} sx={{ px: 1 }}>
                    <FieldLabel>Last Updated By</FieldLabel>
                    <Box>{trainingNotesDetails.ModifiedBy}</Box>
                  </Grid>
                  <Grid item xs={3} sx={{ px: 1 }}>
                    <FieldLabel>Last Updated Date</FieldLabel>
                    <Box>
                      {trainingNotesDetails.ModifiedDate
                        ? formatDate(
                            trainingNotesDetails.ModifiedDate,
                            "MM/DD/YYYY hh:mm A",
                          )
                        : ""}
                    </Box>
                  </Grid>
                </>
              )}
              {isReadOnly && (
                <>
                  <Grid item xs={3} sx={{ px: 1 }}>
                    <FieldLabel>Last Sent By</FieldLabel>
                    <Box>{trainingNotesDetails.ModifiedBy}</Box>
                  </Grid>
                  <Grid item xs={3} sx={{ px: 1 }}>
                    <FieldLabel>Last Sent Date</FieldLabel>
                    <Box>
                      {trainingNotesDetails.ModifiedDate
                        ? formatDate(
                            trainingNotesDetails.ModifiedDate,
                            "MM/DD/YYYY hh:mm A",
                          )
                        : ""}
                    </Box>
                  </Grid>
                </>
              )}
            </Grid>
          </Grid>
          <Grid item xs={4} className={classes.padding1Rem}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-end",
                gap: 1,
              }}
            >
              {/* ASTT-7319: Preview button, per direct stakeholder-demo
                  feedback -- deliberately outside the !isReadOnly check
                  below, since this needs to show for both Draft (still
                  editable) and Sent (read-only) entries, unlike
                  Save/Send which only make sense while still editable.
                  dtrId > 0 means this entry has been saved at least
                  once; nothing to preview before that. */}
              {dtrId > 0 && (
                <CustomOutlinedButton
                  variant="outlined"
                  size="small"
                  onClick={() => setPreviewDialogOpen(true)}
                >
                  Preview Email
                </CustomOutlinedButton>
              )}
              {/* ASTT-7319: replaced two separate, equal-weight buttons
                  with a shared split-button -- Send is now the primary,
                  visually prominent action, Save as Draft is secondary. */}
              {!isReadOnly && (
                <SaveOrSendButton
                  onSendEmail={handleSendEmail}
                  onSaveAsDraft={handleSaveAsDraft}
                  disabled={isReadOnly}
                />
              )}
            </Box>
          </Grid>
        </Grid>
      <EmailPreviewDialog
        open={previewDialogOpen}
        onClose={() => setPreviewDialogOpen(false)}
        fetchPreview={() => previewDtrEmail(dtrId)}
      />
      <SnackBar snack={snack} snackReset={snackReset}></SnackBar>
      <UnsavedChangesBlockerDialog
        blocker={navigationBlocker}
        entityLabel="Training Notes"
      />
    </ThemeProvider>
  );
}

TrainingNotes.propTypes = {
  rosterId: PropTypes.string,
  dtrDate: PropTypes.string,
  onDirtyChange: PropTypes.func,
  classRequestId: PropTypes.string,
  jumpToDtrDate: PropTypes.shape({
    date: PropTypes.string,
    nonce: PropTypes.number,
  }),
  onDtrSaved: PropTypes.func,
  selectedDtrDate: PropTypes.string,
  onSelectedDtrDateChange: PropTypes.func,
};
